import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Filter,
  RefreshCw,
  Search,
  ShieldCheck,
  TimerReset,
  Users
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api-pak.sudindikjt2.site';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const clean = value => String(value ?? '').trim();

const effectiveSubmissionData = row => {
  const proposed = row?.submission_snapshot;
  if (proposed && typeof proposed === 'object' && Object.keys(proposed).length) {
    return proposed;
  }
  return row?.simpeg_snapshot || row?.sipak_snapshot || {};
};

const normalizeGolongan = value => clean(value).toUpperCase().replace(/\s+/g, '');

const fmtDate = value => {
  if (!value) return '-';
  const raw = clean(value).slice(0, 10);
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw || '-';
  return d.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const statusLabel = status => ({
  DRAFT: 'Belum Dikirim',
  SUBMITTED: 'Menunggu Verifikasi Satlak',
  BTS: 'Perlu Perbaikan',
  SATLAK_APPROVED: 'Lolos Satlak · Menunggu Sudin',
  APPROVED: 'Terverifikasi Sudin',
  DEFERRED: 'Dialihkan ke TMT Berikutnya'
}[status] || status || '-');

const statusTone = status => ({
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
  BTS: 'bg-rose-50 text-rose-700 border-rose-200',
  SATLAK_APPROVED: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DEFERRED: 'bg-violet-50 text-violet-700 border-violet-200'
}[status] || 'bg-slate-100 text-slate-700 border-slate-200');

const getUsulanBaru = row => {
  const data = effectiveSubmissionData(row);
  if (row?.btl_from_tmt) return 'BTL';
  return data.usulan_baru_btl || 'BARU';
};

const getKetBtl = row => {
  const data = effectiveSubmissionData(row);
  if (row?.btl_from_tmt) return `BTL dari TMT ${fmtDate(row.btl_from_tmt)}`;
  return data.ket_btl_tmt_sebelumnya || row?.btl_note || data.keterangan_btl || '-';
};

const getSpp = row => {
  const data = effectiveSubmissionData(row);
  const gol = normalizeGolongan(data.golongan);
  if (gol !== 'III/A') return '-';
  return Number(row?.spp_count || 0) > 0 ? 'Sudah' : 'Belum';
};

const officialColumns = [
  ['No', row => row.__no],
  ['Nama', row => effectiveSubmissionData(row).full_name || effectiveSubmissionData(row).nama || '-'],
  ['NIP', row => effectiveSubmissionData(row).nip || row.nip || '-'],
  ['NRK', row => effectiveSubmissionData(row).nrk || '-'],
  ['Gol', row => effectiveSubmissionData(row).golongan || '-'],
  ['TMT Gol.', row => fmtDate(effectiveSubmissionData(row).tmt_golongan)],
  ['MK Tahun', row => effectiveSubmissionData(row).masa_kerja_tahun ?? '-'],
  ['MK Bulan', row => effectiveSubmissionData(row).masa_kerja_bulan ?? '-'],
  ['Pendidikan Terakhir', row => effectiveSubmissionData(row).pendidikan_terakhir || effectiveSubmissionData(row).pendidikan_pangkat || '-'],
  ['Jabatan', row => effectiveSubmissionData(row).jabatan || '-'],
  ['Nama Atasan', row => effectiveSubmissionData(row).nama_atasan || '-'],
  ['NIP Atasan', row => effectiveSubmissionData(row).nip_atasan || '-'],
  ['Unit Kerja', row => effectiveSubmissionData(row).unit_kerja || '-'],
  ['Kecamatan', row => effectiveSubmissionData(row).kecamatan || '-'],
  ['UKPD', row => effectiveSubmissionData(row).ukpd || 'Sudin Pendidikan Jakarta Timur II'],
  ['Jenis KP', row => effectiveSubmissionData(row).jenis_kp || row.jenis_kp || '-'],
  ['SKP 2025', row => effectiveSubmissionData(row).nilai_skp_2025 || '-'],
  ['SKP 2024', row => effectiveSubmissionData(row).nilai_skp_2024 || '-'],
  ['Usulan Baru / BTL', row => getUsulanBaru(row)],
  ['Ket. BTL Sebelumnya', row => getKetBtl(row)],
  ['SPP', row => getSpp(row)],
  ['Keterangan BTL', row => row.status === 'BTS' ? (row.reviewer_note || 'Perlu perbaikan') : (row.btl_note || effectiveSubmissionData(row).keterangan_btl || '-')]
];

function StatCard({ icon: Icon, value, label, tone = 'slate' }) {
  const cls = {
    slate: 'bg-white border-slate-200 text-slate-900',
    blue: 'bg-blue-50 border-blue-100 text-blue-900',
    amber: 'bg-amber-50 border-amber-100 text-amber-900',
    green: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    red: 'bg-rose-50 border-rose-100 text-rose-900'
  }[tone];

  return (
    <div className={`border rounded-2xl p-4 ${cls}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-2xl font-black tracking-tight">{value}</div>
          <div className="text-xs mt-1 opacity-70 font-semibold">{label}</div>
        </div>
        <Icon size={19} className="opacity-60" />
      </div>
    </div>
  );
}

async function downloadWithToken(url, fallbackName) {
  const response = await fetch(url, { headers: authHeaders() });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.msg || 'Excel belum dapat diunduh.');
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 3000);
}

export default function RekapUsulanPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('NON_DRAFT');
  const [kecamatan, setKecamatan] = useState('ALL');
  const [jenisKp, setJenisKp] = useState('ALL');
  const [unitKerja, setUnitKerja] = useState('');

  const role = clean(localStorage.getItem('userRole') || localStorage.getItem('role')).toLowerCase();
  const isSatlak = role === 'satlak';

  const loadPeriods = async () => {
    try {
      setError('');
      const response = await fetch(`${API}/api/kp/periods`, { headers: authHeaders() });
      const payload = await response.json().catch(() => []);
      if (!response.ok) throw new Error(payload.msg || 'Gagal memuat periode.');

      const data = Array.isArray(payload) ? payload : [];
      setPeriods(data);

      if (!periodId && data.length) {
        const active = data.find(item => String(item.status || '').toUpperCase() === 'ACTIVE') || data[0];
        setPeriodId(String(active.id));
      }
    } catch (e) {
      setError(e.message || 'Gagal memuat periode.');
      setLoading(false);
    }
  };

  const loadRows = async () => {
    if (!periodId) return;

    setLoading(true);
    setError('');

    try {
      // Sengaja memakai fetch biasa, bukan test_user_id dari Simulasi Guru.
      // Rekap harus selalu membaca daftar periode secara keseluruhan sesuai role login.
      const response = await fetch(`${API}/api/kp/submissions?period_id=${encodeURIComponent(periodId)}`, {
        headers: authHeaders()
      });
      const payload = await response.json().catch(() => []);
      if (!response.ok) throw new Error(payload.msg || 'Gagal memuat rekap usulan.');
      setRows(Array.isArray(payload) ? payload : []);
    } catch (e) {
      setRows([]);
      setError(e.message || 'Gagal memuat rekap usulan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (periodId) loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId]);

  const kecamatanOptions = useMemo(() => {
    const values = rows
      .map(row => clean(effectiveSubmissionData(row).kecamatan))
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'id'));
  }, [rows]);

  const jenisKpOptions = useMemo(() => {
    const values = rows
      .map(row => clean(effectiveSubmissionData(row).jenis_kp || row.jenis_kp))
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'id'));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    const unitNeedle = unitKerja.toLowerCase().trim();

    return rows
      .filter(row => {
        const data = effectiveSubmissionData(row);
        const searchable = [
          data.full_name,
          data.nama,
          data.nip,
          row.nip,
          data.nrk,
          data.unit_kerja,
          data.kecamatan,
          data.jabatan,
          data.nama_atasan,
          data.nip_atasan
        ].map(clean).join(' ').toLowerCase();

        const matchQ = !needle || searchable.includes(needle);
        const matchUnit = !unitNeedle || clean(data.unit_kerja).toLowerCase().includes(unitNeedle);
        const matchKecamatan = kecamatan === 'ALL' || clean(data.kecamatan) === kecamatan;
        const matchJenis = jenisKp === 'ALL' || clean(data.jenis_kp || row.jenis_kp) === jenisKp;

        let matchStatus = true;
        if (status === 'NON_DRAFT') matchStatus = row.status !== 'DRAFT';
        else if (status !== 'ALL') matchStatus = row.status === status;

        return matchQ && matchUnit && matchKecamatan && matchJenis && matchStatus;
      })
      .sort((a, b) => {
        const aa = effectiveSubmissionData(a);
        const bb = effectiveSubmissionData(b);
        return [aa.kecamatan, aa.unit_kerja, aa.full_name]
          .map(clean).join('|')
          .localeCompare([bb.kecamatan, bb.unit_kerja, bb.full_name].map(clean).join('|'), 'id');
      })
      .map((row, index) => ({ ...row, __no: index + 1 }));
  }, [rows, q, unitKerja, kecamatan, jenisKp, status]);

  const period = periods.find(item => String(item.id) === String(periodId));
  const submittedRows = rows.filter(row => row.status !== 'DRAFT');

  const counts = {
    total: submittedRows.length,
    satlak: rows.filter(row => row.status === 'SUBMITTED').length,
    perbaikan: rows.filter(row => row.status === 'BTS').length,
    sudin: rows.filter(row => row.status === 'SATLAK_APPROVED').length,
    selesai: rows.filter(row => row.status === 'APPROVED').length
  };

  const resetFilters = () => {
    setQ('');
    setStatus('NON_DRAFT');
    setKecamatan('ALL');
    setJenisKp('ALL');
    setUnitKerja('');
  };

  const handleDownload = async mode => {
    if (!periodId || downloading) return;
    setDownloading(mode);

    try {
      const params = new URLSearchParams({ period_id: String(periodId) });

      if (mode === 'approved') {
        params.set('status', 'APPROVED');
      } else {
        params.set('exclude_draft', '1');
      }

      await downloadWithToken(
        `${API}/api/kp/export.xlsx?${params.toString()}`,
        mode === 'approved' ? 'Daftar_Nominatif_KP_Siap_Suban.xlsx' : 'Daftar_Nominatif_KP.xlsx'
      );
    } catch (e) {
      alert(e.message || 'Excel belum dapat diunduh.');
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white rounded-3xl p-5 md:p-7 shadow-sm overflow-hidden relative">
        <div className="absolute -right-16 -top-20 w-64 h-64 rounded-full bg-white/5" />
        <div className="relative flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-bold mb-3">
              <FileSpreadsheet size={14} /> DAFTAR NOMINATIF
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Rekap Usulan Kenaikan Pangkat</h1>
            <p className="text-sm text-slate-300 mt-2 max-w-3xl leading-relaxed">
              Lembar rekap seluruh pegawai yang mengusulkan kenaikan pangkat. Struktur data mengikuti format 22 kolom daftar usulan untuk pengiriman ke Suban.
            </p>
            {isSatlak && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-400/10 border border-blue-300/20 text-blue-100 text-xs font-semibold">
                <ShieldCheck size={15} /> Data otomatis dibatasi sesuai kecamatan akun Satlak.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleDownload('all')}
              disabled={!periodId || Boolean(downloading)}
              className="h-11 px-4 rounded-xl bg-white/10 hover:bg-white/15 disabled:opacity-50 border border-white/15 font-bold text-sm inline-flex items-center gap-2"
            >
              <Download size={16} />
              {downloading === 'all' ? 'Menyiapkan...' : 'Excel Semua Usulan'}
            </button>
            <button
              onClick={() => handleDownload('approved')}
              disabled={!periodId || Boolean(downloading)}
              className="h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-black text-sm inline-flex items-center gap-2 shadow-lg shadow-emerald-950/20"
            >
              <CheckCircle2 size={16} />
              {downloading === 'approved' ? 'Menyiapkan...' : 'Excel Siap ke Suban'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} value={counts.total} label="Sudah Mengusulkan" />
        <StatCard icon={TimerReset} value={counts.satlak} label="Menunggu Satlak" tone="blue" />
        <StatCard icon={AlertTriangle} value={counts.perbaikan} label="Perlu Perbaikan" tone="red" />
        <StatCard icon={Filter} value={counts.sudin} label="Menunggu Sudin" tone="amber" />
        <StatCard icon={CheckCircle2} value={counts.selesai} label="Terverifikasi Sudin" tone="green" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="grid md:grid-cols-2 xl:grid-cols-[230px_1.4fr_190px_190px_1fr_auto_auto] gap-3">
          <select
            value={periodId}
            onChange={e => setPeriodId(e.target.value)}
            className="h-11 border border-slate-200 rounded-xl px-3 bg-white font-semibold text-sm"
          >
            {periods.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>

          <div className="relative">
            <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Cari nama, NIP, NRK, jabatan, atasan..."
              className="w-full h-11 border border-slate-200 rounded-xl pl-9 pr-3 text-sm"
            />
          </div>

          <select value={status} onChange={e => setStatus(e.target.value)} className="h-11 border border-slate-200 rounded-xl px-3 bg-white text-sm">
            <option value="NON_DRAFT">Sudah mengusulkan</option>
            <option value="ALL">Semua termasuk draf</option>
            <option value="SUBMITTED">Menunggu Satlak</option>
            <option value="BTS">Perlu Perbaikan</option>
            <option value="SATLAK_APPROVED">Menunggu Sudin</option>
            <option value="APPROVED">Terverifikasi Sudin</option>
            <option value="DEFERRED">Dialihkan TMT</option>
            <option value="DRAFT">Belum Dikirim</option>
          </select>

          <select
            value={kecamatan}
            onChange={e => setKecamatan(e.target.value)}
            disabled={isSatlak && kecamatanOptions.length <= 1}
            className="h-11 border border-slate-200 rounded-xl px-3 bg-white text-sm disabled:bg-slate-50"
          >
            <option value="ALL">Semua Kecamatan</option>
            {kecamatanOptions.map(item => <option key={item} value={item}>{item}</option>)}
          </select>

          <input
            value={unitKerja}
            onChange={e => setUnitKerja(e.target.value)}
            placeholder="Filter unit kerja..."
            className="h-11 border border-slate-200 rounded-xl px-3 text-sm"
          />

          <select value={jenisKp} onChange={e => setJenisKp(e.target.value)} className="h-11 border border-slate-200 rounded-xl px-3 bg-white text-sm">
            <option value="ALL">Semua Jenis KP</option>
            {jenisKpOptions.map(item => <option key={item} value={item}>{item}</option>)}
          </select>

          <div className="flex gap-2">
            <button onClick={loadRows} title="Muat ulang" className="h-11 w-11 rounded-xl bg-slate-100 hover:bg-slate-200 inline-flex items-center justify-center">
              <RefreshCw size={16} />
            </button>
            <button onClick={resetFilters} title="Reset filter" className="h-11 w-11 rounded-xl border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center">
              <Filter size={16} />
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>Menampilkan <b className="text-slate-800">{filtered.length}</b> data dari {rows.length} record periode.</span>
          <span>{period?.name || 'Periode belum dipilih'} · Export “Siap ke Suban” hanya mengambil status <b>TERVERIFIKASI SUDIN</b>.</span>
        </div>
      </div>

      {error && (
        <div className="border border-rose-200 bg-rose-50 text-rose-800 rounded-2xl p-4 text-sm">
          <b>Rekap belum dapat dimuat.</b> {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-3">
          <div>
            <div className="font-black text-sm text-slate-800">Daftar Nominatif Usulan Kenaikan Pangkat</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Kolom 1–22 mengikuti struktur file usulan resmi.</div>
          </div>
          <div className="text-[11px] font-bold text-slate-500 hidden md:block">Geser tabel ke kanan untuk melihat seluruh kolom</div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-slate-500">Memuat daftar usulan...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">Tidak ada data yang sesuai filter.</div>
        ) : (
          <div className="overflow-auto max-h-[68vh]">
            <table className="text-xs min-w-[3500px] w-full border-collapse">
              <thead className="sticky top-0 z-20 bg-slate-900 text-white">
                <tr>
                  {officialColumns.map(([label]) => (
                    <th key={label} className="border border-slate-700 px-2.5 py-3 text-left align-bottom font-bold whitespace-normal min-w-[105px]">
                      {label}
                    </th>
                  ))}
                  <th className="border border-slate-700 px-2.5 py-3 text-left min-w-[190px]">Status Alur</th>
                  <th className="border border-slate-700 px-2.5 py-3 text-left min-w-[120px]">AK</th>
                  <th className="border border-slate-700 px-2.5 py-3 text-left min-w-[110px]">Dokumen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.id} className="odd:bg-white even:bg-slate-50/70 hover:bg-blue-50/70">
                    {officialColumns.map(([label, getter]) => {
                      const value = getter(row);
                      return (
                        <td key={label} className="border border-slate-200 px-2.5 py-2.5 align-top whitespace-normal">
                          {value === 0 ? 0 : (value || '-')}
                        </td>
                      );
                    })}
                    <td className="border border-slate-200 px-2.5 py-2.5 align-top">
                      <span className={`inline-flex px-2.5 py-1 rounded-full border font-bold whitespace-nowrap ${statusTone(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td className="border border-slate-200 px-2.5 py-2.5 align-top">
                      <span className={`font-bold ${row.ak_override || clean(row.ak_status).toLowerCase() === 'cukup' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {row.ak_override ? 'Override' : (row.ak_status || 'Belum dicek')}
                      </span>
                    </td>
                    <td className="border border-slate-200 px-2.5 py-2.5 align-top">
                      <b>{Number(row.document_count || 0)}</b> file
                      {Number(row.bts_document_count || 0) > 0 && (
                        <div className="text-rose-600 mt-1">{row.bts_document_count} BTS</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
